import { useCallback } from 'react';
import { ITcoDeviceSelection, ITcoLocations, ITCOSubmission } from 'types';
import { tcoFieldsIds } from 'utils/defaultsAndConstants';
import { useQueryData } from './useQueryData';
import { useUpdateTcoDeviceSelection } from './apiHooks/useUpdateTcoDeviceSelection';
import { useRefreshTcoUnderlaySelections } from './useRefreshTcoUnderlaySelections';

interface IRefreshDeviceSelectionsConfig {
  latestSubmission?: Partial<ITCOSubmission>;
  tcoSubmissions: ITCOSubmission[];
  tcoDeviceSelections: ITcoDeviceSelection[];
  tcoLocations?: ITcoLocations[];
  refresh?: Boolean;
}

// Resets TCO Device Selections with initial pricing and latest formula
export const useRefreshTcoDeviceSelections = (estimateId: string) => {
  const { allTcoSpectrumProducts, allTcoDiyProducts, userId } = useQueryData();
  const { updateTcoDeviceSelection } = useUpdateTcoDeviceSelection(estimateId);
  const { refreshUnderlaySelections } = useRefreshTcoUnderlaySelections();

  const {
    varDiscountFieldId,
    salesTaxFieldId,
    shippingHandlingFieldId,
    termsFieldId,
  } = tcoFieldsIds;

  const refreshDeviceSelections = useCallback(
    ({
      latestSubmission,
      tcoDeviceSelections = [],
      tcoSubmissions = [],
      tcoLocations = [],
      refresh = false,
    }: IRefreshDeviceSelectionsConfig) => {
      const termFieldValue = tcoSubmissions.find(
        (s) => s.fieldId === termsFieldId
      )?.answer;

      const varDiscount =
        1 -
        Number(
          tcoSubmissions.find((s) => s.fieldId === varDiscountFieldId)?.answer
        ) /
          100;

      const salesTax =
        Number(
          tcoSubmissions.find((s) => s.fieldId === salesTaxFieldId)?.answer
        ) / 100;

      const shippingHandeling =
        Number(
          tcoSubmissions.find((s) => s.fieldId === shippingHandlingFieldId)
            ?.answer
        ) / 100;
      let resetSpectrumSelection = false;
      tcoDeviceSelections.forEach((selection) => {
        if (
          (latestSubmission && latestSubmission.fieldId === termsFieldId) ||
          refresh
        ) {
          resetSpectrumSelection = true;
          var monthlyCost = termFieldValue
            ? allTcoSpectrumProducts
                .find((p) => p.id === selection.spectrumId)
                ?.prices.find((p) => p.term === termFieldValue)?.price
            : selection.monthlyCost;

          var installationCost = termFieldValue
            ? allTcoSpectrumProducts.find((p) => p.id === selection.spectrumId)
                ?.rateNrc
            : selection.installationCost;
        }
        var hardwareCost = (
          allTcoDiyProducts.find((p) => p.id === selection.diyId)?.rateNrc! *
            varDiscount +
          allTcoDiyProducts.find((p) => p.id === selection.diyId)?.rateNrc! *
            varDiscount *
            salesTax +
          allTcoDiyProducts.find((p) => p.id === selection.diyId)?.rateNrc! *
            varDiscount *
            shippingHandeling
        ).toFixed(2);

        var licensingCost = (
          allTcoDiyProducts
            .find((p) => p.id === selection.diyId)
            ?.prices.find((p) => p.term === termFieldValue)?.price! *
            varDiscount +
          allTcoDiyProducts
            .find((p) => p.id === selection.diyId)
            ?.prices.find((p) => p.term === termFieldValue)?.price! *
            varDiscount *
            salesTax +
          allTcoDiyProducts
            .find((p) => p.id === selection.diyId)
            ?.prices.find((p) => p.term === termFieldValue)?.price! *
            varDiscount *
            shippingHandeling
        ).toFixed(2);

        updateTcoDeviceSelection({
          id: selection.id,
          estimateId: selection.estimateId,
          name: selection.name,
          diyId: selection.diyId,
          spectrumId: selection.spectrumId,
          quantity: selection.quantity,
          monthlyCost: resetSpectrumSelection
            ? monthlyCost!
            : selection.monthlyCost,
          installationCost: resetSpectrumSelection
            ? installationCost!
            : selection.installationCost,
          hardwareCost: Number(hardwareCost!),
          licenseCost: Number(licensingCost!),
          updatedBy: userId,
        });
      });

      if (
        latestSubmission &&
        latestSubmission.fieldId === termsFieldId &&
        tcoLocations.length
      ) {
        refreshUnderlaySelections({
          latestSubmission,
          tcoLocations,
          tcoSubmissions,
        });
      }
    },
    [
      allTcoSpectrumProducts,
      allTcoDiyProducts,
      termsFieldId,
      varDiscountFieldId,
      shippingHandlingFieldId,
      salesTaxFieldId,
      updateTcoDeviceSelection,
      refreshUnderlaySelections,
      userId,
    ]
  );

  return { refreshDeviceSelections };
};
